// Reunderwriting Module (Tier 3) PR 4 — executive Failure Tracking
// surface scoped to reunderwriting cases. URL-synced filters; CSV export
// is rate-limited 1/60s server-side, mirrored client-side via a cooldown.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertOctagon, Download } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import {
  useReunderwritingFailures,
  useReunderwritingFailure,
  useResolveReunderwritingFailure,
  useMarkReunderwritingFailureReviewed,
} from "@/hooks/reunderwriting/useReunderwriting";
import { reunderwritingFailureTrackingApi } from "@/api/reunderwriting";
import {
  REUNDERWRITING_PERMISSIONS,
  type CriteriaLane,
  type FailureStatus,
  type ReunderwritingFailureFilter,
} from "@/types/reunderwriting";
import { ReunderwritingFailureFilterSidebar } from "./failures/FailureFilterSidebar";
import { ReunderwritingFailureList } from "./failures/FailureList";
import { ReunderwritingFailureDetailDrawer } from "./failures/FailureDetailDrawer";

const LANES: CriteriaLane[] = ["A1", "A2", "A3", "A4"];
const STATUSES: FailureStatus[] = ["Open", "Reviewed", "Resolved"];

function parseStatus(v: string | null): FailureStatus | undefined {
  return STATUSES.find((s) => s === v);
}
function parseLane(v: string | null): CriteriaLane | undefined {
  return LANES.find((l) => l === v);
}

export function ReunderwritingFailureTrackingPage() {
  const flagOn = useReunderwritingV1();
  const { hasPermission } = useAuth();
  const canResolve = hasPermission(REUNDERWRITING_PERMISSIONS.ResolveFailure);

  const [params, setParams] = useSearchParams();
  const status = parseStatus(params.get("status"));
  const lane = parseLane(params.get("lane"));
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const caseId = params.get("caseId") ?? undefined;
  const page = Math.max(1, Number(params.get("page") ?? "1"));
  const pageSize = Math.min(100, Math.max(1, Number(params.get("pageSize") ?? "25")));
  const sort = params.get("sort") ?? "createdAt:desc";

  const [openId, setOpenId] = useState<string | null>(null);
  const [csvCooldownUntil, setCsvCooldownUntil] = useState<number | null>(null);

  const query: ReunderwritingFailureFilter = useMemo(
    () => ({ status, lane, caseId, from, to, page, pageSize, sort }),
    [status, lane, caseId, from, to, page, pageSize, sort],
  );

  const failuresQ = useReunderwritingFailures(flagOn ? query : undefined);
  const detailQ = useReunderwritingFailure(openId ?? undefined);
  const resolveM = useResolveReunderwritingFailure();
  const reviewM = useMarkReunderwritingFailureReviewed();

  const setFilter = useCallback(
    (next: Partial<Record<string, string | null>>) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === undefined || v === "") merged.delete(k);
        else merged.set(k, v);
      }
      if (Object.keys(next).some((k) => k !== "page")) merged.set("page", "1");
      setParams(merged, { replace: false });
    },
    [params, setParams],
  );

  const handleExportCsv = useCallback(() => {
    const now = Date.now();
    if (csvCooldownUntil && now < csvCooldownUntil) return;
    const url = reunderwritingFailureTrackingApi.exportCsvUrl(query);
    window.open(url, "_blank");
    setCsvCooldownUntil(now + 60_000);
  }, [csvCooldownUntil, query]);

  useEffect(() => {
    if (!csvCooldownUntil) return;
    const remaining = csvCooldownUntil - Date.now();
    if (remaining <= 0) {
      setCsvCooldownUntil(null);
      return;
    }
    const t = setTimeout(() => setCsvCooldownUntil(null), remaining);
    return () => clearTimeout(t);
  }, [csvCooldownUntil]);

  if (!flagOn) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Reunderwriting disabled</h2>
        <p className="text-small text-gray-mid">
          The Reunderwriting module is not enabled in this environment.
        </p>
      </Card>
    );
  }

  const csvDisabled = !!csvCooldownUntil && Date.now() < csvCooldownUntil;

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-failure-tracking">
      <PageHeader
        eyebrow="Reunderwriting"
        title="Failure Tracking"
        description="Item-level and lane-level analyst failures from reunderwriting cases."
        actions={
          <Button
            variant="secondary"
            onClick={handleExportCsv}
            disabled={csvDisabled}
            title={csvDisabled ? "You exported in the last 60 seconds." : "Download filtered failures as CSV"}
            aria-label="Export filtered reunderwriting failures as CSV"
          >
            <Download size={16} aria-hidden />
            <span className="ml-space-1">
              {csvDisabled ? "Exported recently" : "Export CSV"}
            </span>
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-4">
        <aside className="lg:col-span-3">
          <ReunderwritingFailureFilterSidebar
            status={status ?? null}
            lane={lane ?? null}
            from={from ?? ""}
            to={to ?? ""}
            onChange={setFilter}
          />
        </aside>

        <main className="lg:col-span-9">
          <Card className="p-space-4">
            {failuresQ.isLoading ? (
              <div role="status" aria-live="polite" className="text-gray-mid">
                Loading failures…
              </div>
            ) : failuresQ.isError ? (
              <div role="alert" className="flex items-center gap-space-2 text-status-error">
                <AlertOctagon size={18} aria-hidden />
                <span>Failed to load failures.</span>
              </div>
            ) : (
              <ReunderwritingFailureList
                page={
                  failuresQ.data ?? { items: [], page: 1, pageSize, totalCount: 0 }
                }
                sort={sort}
                onSortChange={(s) => setFilter({ sort: s })}
                onPageChange={(p) => setFilter({ page: String(p) })}
                onPageSizeChange={(s) => setFilter({ pageSize: String(s) })}
                onOpen={(id) => setOpenId(id)}
              />
            )}

            {failuresQ.data?.items.length === 0 && !failuresQ.isLoading && (
              <div className="mt-space-3 text-gray-mid">
                <p>No failures match your filters.</p>
                <button
                  type="button"
                  className="text-gold underline mt-space-2"
                  onClick={() =>
                    setFilter({
                      status: null,
                      lane: null,
                      from: null,
                      to: null,
                      page: "1",
                    })
                  }
                >
                  Clear filters
                </button>
              </div>
            )}
          </Card>
        </main>
      </div>

      <ReunderwritingFailureDetailDrawer
        open={!!openId}
        failure={detailQ.data ?? null}
        canResolve={canResolve}
        isLoading={detailQ.isLoading}
        onClose={() => setOpenId(null)}
        onMarkReviewed={async (notes) => {
          if (!openId) return;
          await reviewM.mutateAsync({ id: openId, notes });
          setOpenId(null);
        }}
        onResolve={async (resolutionNotes) => {
          if (!openId) return;
          await resolveM.mutateAsync({
            id: openId,
            body: { resolutionNotes, certifiedResolved: true },
          });
          setOpenId(null);
        }}
      />

      <span aria-live="polite" className="sr-only">
        {failuresQ.data
          ? `${failuresQ.data.totalCount} failures match the current filters.`
          : ""}
      </span>

      <span className="sr-only">
        <Badge status="error" /> Open · <Badge status="review" /> Reviewed ·{" "}
        <Badge status="finalized" /> Resolved
      </span>
    </div>
  );
}
